import { format } from "date-fns";
import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty";

type AuditLog = {
  id: string;
  action: string;
  createdAt: string;
  details?: unknown;
  userId?: string | null;
};

function formatAuditAction(action: string): string {
  return action.replace(/_/g, " ");
}

function formatChangeLabel(value: string): string {
  return value
    .split(".")
    .map((segment) =>
      segment
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    )
    .join(" → ");
}

type ChangeDetail = {
  path: string;
  changeType: "added" | "removed" | "updated";
  previous: string | undefined;
  next: string | undefined;
};

function formatChangeValue(value: string | undefined) {
  if (!value) return "—";
  return value;
}

function AuditDetails({
  action,
  details,
}: {
  action: string;
  details: unknown;
}): ReactNode {
  if (!details || typeof details !== "object") {
    return (
      <div className="text-sm text-muted-foreground">No details added</div>
    );
  }

  const data = details as Record<string, unknown>;

  switch (action) {
    case "created":
      if (data.templateId && typeof data.templateId === "string") {
        return (
          <div className="text-sm text-muted-foreground">
            Template: <span className="font-medium">{data.templateId}</span>
          </div>
        );
      }
      break;

    case "updated": {
      const changes: ChangeDetail[] = Array.isArray(data.changes)
        ? (data.changes as unknown[]).flatMap((value) => {
            if (typeof value !== "object" || !value) return [];
            const record = value as Record<string, unknown>;
            if (typeof record.path !== "string") return [];
            const changeType =
              record.changeType === "added" ||
              record.changeType === "removed" ||
              record.changeType === "updated"
                ? record.changeType
                : "updated";

            return [
              {
                path: record.path,
                changeType,
                previous:
                  typeof record.previous === "string"
                    ? record.previous
                    : undefined,
                next: typeof record.next === "string" ? record.next : undefined,
              },
            ];
          })
        : [];

      if (changes.length > 0) {
        return (
          <div className="space-y-1 text-sm text-muted-foreground">
            {changes.map((change) => {
              const label = formatChangeLabel(change.path);

              switch (change.changeType) {
                case "added":
                  return (
                    <div key={`${change.path}-added`}>
                      Added{" "}
                      <span className="font-medium text-foreground">
                        {label}
                      </span>{" "}
                      (now{" "}
                      <span className="font-medium text-foreground">
                        {formatChangeValue(change.next)}
                      </span>
                      )
                    </div>
                  );
                case "removed":
                  return (
                    <div key={`${change.path}-removed`}>
                      Removed{" "}
                      <span className="font-medium text-foreground">
                        {label}
                      </span>{" "}
                      (was{" "}
                      <span className="font-medium text-foreground">
                        {formatChangeValue(change.previous)}
                      </span>
                      )
                    </div>
                  );
                default:
                  return (
                    <div key={`${change.path}-updated`}>
                      <span className="font-medium text-foreground">
                        {label}
                      </span>{" "}
                      changed from{" "}
                      <span className="font-medium text-foreground">
                        {formatChangeValue(change.previous)}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium text-foreground">
                        {formatChangeValue(change.next)}
                      </span>
                    </div>
                  );
              }
            })}
          </div>
        );
      }

      const fallbackFields = Array.isArray(data.changedFields)
        ? (data.changedFields as unknown[])
            .filter((value): value is string => typeof value === "string")
            .map((value) => value.replace(/_/g, " "))
        : [];
      const labels = fallbackFields;

      if (labels.length === 0) {
        break;
      }

      return (
        <div className="text-sm text-muted-foreground">
          Updated:{" "}
          <span className="font-medium text-foreground">
            {labels.join(", ")}
          </span>
        </div>
      );
    }

    case "submitted":
      if (data.note && typeof data.note === "string") {
        return (
          <div className="space-y-1">
            <div className="text-sm font-medium">Submission note:</div>
            <div className="text-sm text-muted-foreground">
              {data.note as string}
            </div>
          </div>
        );
      }
      break;

    case "approved":
      return (
        <div className="space-y-2">
          {typeof data.note === "string" ? (
            <div className="space-y-1">
              <div className="text-sm font-medium">Approval note:</div>
              <div className="text-sm text-muted-foreground">{data.note}</div>
            </div>
          ) : null}
          {typeof data.authorisation === "object" && data.authorisation ? (
            <div className="space-y-1">
              <div className="text-sm font-medium">Authorisation details:</div>
              {Object.entries(data.authorisation).length === 0 ? (
                <div className="text-sm text-muted-foreground">No details</div>
              ) : (
                <div className="bg-muted/20 p-2 rounded space-y-1">
                  {Object.entries(
                    data.authorisation as Record<string, unknown>
                  ).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="text-muted-foreground capitalize">
                        {key.replace(/_/g, " ")}:
                      </span>
                      <span className="font-medium">
                        {typeof value === "string"
                          ? value
                          : JSON.stringify(value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      );

    case "rejected":
      return (
        <div className="space-y-2">
          {typeof data.note === "string" ? (
            <div className="space-y-1">
              <div className="text-sm font-medium">Rejection note:</div>
              <div className="text-sm text-muted-foreground">{data.note}</div>
            </div>
          ) : null}
          {(() => {
            const rejection =
              typeof data.rejection === "object" && data.rejection
                ? (data.rejection as Record<string, unknown>)
                : null;
            if (!rejection || typeof rejection.reason !== "string") {
              return null;
            }
            return (
              <div className="space-y-1">
                <div className="text-sm font-medium">Rejection reason:</div>
                <div className="text-sm text-muted-foreground">
                  {rejection.reason}
                </div>
              </div>
            );
          })()}
        </div>
      );
  }

  // Fallback: show JSON for unknown actions
  return (
    <pre className="text-sm text-muted-foreground bg-muted/20 p-2 rounded overflow-x-auto">
      {JSON.stringify(details, null, 2)}
    </pre>
  );
}

interface AuditLogCardProps {
  auditLogs: AuditLog[];
  variant?: "simple" | "timeline";
}

export function AuditLogCard({
  auditLogs,
  variant = "simple",
}: AuditLogCardProps) {
  if (auditLogs.length === 0) {
    return (
      <Empty>
        <EmptyTitle className="text-lg">No activity yet</EmptyTitle>
        <EmptyDescription className="text-sm">
          Once this form is updated, the timeline will appear here.
        </EmptyDescription>
      </Empty>
    );
  }

  if (variant === "timeline") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Audit log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-2 top-1 bottom-0 w-0.5 bg-border" />

            <div className="space-y-6">
              {auditLogs.map((log) => (
                <div key={log.id} className="relative flex gap-4">
                  {/* Timeline dot */}
                  <div className="relative z-10 flex h-4 w-4 top-1 items-center justify-center rounded-full bg-background border-2 border-border">
                    <div className="h-1.5 w-1.5 rounded-full bg-foreground" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 space-y-2 pb-2">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-muted-foreground uppercase">
                        {formatAuditAction(log.action)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(log.createdAt), "PPpp")}
                      </span>
                    </div>
                    <div className="bg-card border border-border px-4 py-2 rounded-md">
                      <AuditDetails action={log.action} details={log.details} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Simple variant (default)
  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit log</CardTitle>
      </CardHeader>
      <CardContent>
        {auditLogs.length === 0 ? (
          <Empty>
            <EmptyTitle>No events recorded</EmptyTitle>
            <EmptyDescription>
              Key updates to this form will show here.
            </EmptyDescription>
          </Empty>
        ) : (
          <div className="space-y-3">
            {auditLogs.map((log) => (
              <div
                key={log.id}
                className="rounded-md border bg-muted/20 p-3 text-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">
                    {formatAuditAction(log.action)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(log.createdAt), "PPpp")}
                  </span>
                </div>
                <AuditDetails action={log.action} details={log.details} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
